import React, { useEffect, useState } from 'react';
import { settingsAPI, adminProductsAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

const defaultState = {
  title_line1: '',
  title_line2: '',
  subtitle: '',
  align: 'center',
  veil_intensity: 70,
  hero_image_url: '',
  hero_image_format: '',
  hero_image_quality: 85,
  hero_image_width: 1920,
  font_family: 'system',
  title1_color: '#111111',
  title2_color: '#111111',
  subtitle_color: '#111111',
};

const fontMap = {
  system: 'system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif',
  serif: 'Georgia, Times New Roman, serif',
  mono: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
};

const AdminSettingsPage = () => {
  const [draft, setDraft] = useState(defaultState);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const s = await settingsAPI.adminGet();
      const d = s?.draft || {};
      setDraft({ ...defaultState, ...d });
    } catch (e) {
      setMessage('Erreur de chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      setLoading(true);
      await settingsAPI.adminUpdate(draft);
      setMessage('Brouillon enregistré');
    } catch (e) {
      setMessage("Échec d'enregistrement");
    } finally { setLoading(false); }
  };
  const publish = async () => {
    try {
      setLoading(true);
      await settingsAPI.adminPublish();
      setMessage('Paramètres publiés');
    } catch (e) { setMessage('Échec de publication'); }
    finally { setLoading(false); }
  };

  const uploadHero = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setLoading(true);
      const { url } = await adminProductsAPI.upload(file);
      setDraft(d => ({ ...d, hero_image_url: url }));
      setMessage('Image mise à jour');
    } catch { setMessage("Échec d'upload"); }
    finally { setLoading(false); }
  };

  const pickFromGallery = async () => {
    try {
      setLoading(true);
      const items = await adminProductsAPI.listUploads();
      if (items.length > 0) {
        setDraft(d => ({ ...d, hero_image_url: items[0].url }));
        setMessage('Image choisie');
      }
    } catch { setMessage('Galerie indisponible'); }
    finally { setLoading(false); }
  };

  const convertHero = async () => {
    if (!draft.hero_image_url) { setMessage('Choisissez une image'); return; }
    try {
      setLoading(true);
      const fmt = draft.hero_image_format || 'webp';
      const quality = draft.hero_image_quality || 85;
      const width = draft.hero_image_width || undefined;
      const res = await settingsAPI.transformImage({ source_url: draft.hero_image_url, fmt, quality, width });
      setDraft(d => ({ ...d, hero_image_url: res.url }));
      setMessage(`Image convertie en ${res.format}`);
    } catch { setMessage('Conversion échouée'); }
    finally { setLoading(false); }
  };

  const heroPreview = (
    <div className="relative w-full h-[60vh] rounded-xl overflow-hidden border">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${draft.hero_image_url || '/assets/hero/exemple-parfait.webp'}')` }} />
      <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, rgba(255,255,255,${Math.min(0.95, (draft.veil_intensity+25)/100)}), rgba(255,244,178,${Math.min(0.85, draft.veil_intensity/100)}), rgba(255,255,255,0))` }} />
      <div className="relative z-10 h-full flex items-center">
        <div className={`max-w-4xl px-6 ${draft.align==='left' ? 'text-left ml-0' : 'text-center mx-auto'}`} style={{ fontFamily: fontMap[draft.font_family] }}>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold drop-shadow mb-4 leading-tight" style={{ color: draft.title1_color }}>
            {draft.title_line1 || 'Solutions Énergétiques'}
            <span className="block drop-shadow" style={{ color: draft.title2_color }}>{draft.title_line2 || 'Haute Performance'}</span>
          </h1>
          <p className="text-xl sm:text-2xl drop-shadow max-w-3xl leading-relaxed mx-auto" style={{ color: draft.subtitle_color }}>
            {draft.subtitle || "Distributeur et Représentant Officiel EXCLUSIF PowMr spécialisé en batteries lithium et convertisseurs hybrides. L'excellence sans compromis pour vos projets énergétiques en France et en Afrique."}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-white border rounded-xl p-6 shadow-md">
      <h2 className="text-lg font-semibold mb-4">Paramètres du Héros</h2>
      {message && <div className="text-sm mb-3">{message}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-700 mb-1">Titre (ligne 1)</label>
          <Input value={draft.title_line1} onChange={(e)=>setDraft(d=>({...d, title_line1: e.target.value}))} />
        </div>
        <div>
          <label className="block text-sm text-gray-700 mb-1">Titre (ligne 2)</label>
          <Input value={draft.title_line2} onChange={(e)=>setDraft(d=>({...d, title_line2: e.target.value}))} />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm text-gray-700 mb-1">Sous-titre</label>
          <Textarea rows={3} value={draft.subtitle} onChange={(e)=>setDraft(d=>({...d, subtitle: e.target.value}))} />
        </div>

        <div>
          <label className="block text-sm text-gray-700 mb-1">Alignement</label>
          <select className="w-full border rounded h-10 px-3" value={draft.align} onChange={(e)=>setDraft(d=>({...d, align: e.target.value}))}>
            <option value="left">Gauche</option>
            <option value="center">Centré</option>
          </select>
        </div>
        <div>
          <label className="block text-sm text-gray-700 mb-1">Voile (0-100)</label>
          <Input type="number" min="0" max="100" value={draft.veil_intensity} onChange={(e)=>setDraft(d=>({...d, veil_intensity: Number(e.target.value)}))} />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm text-gray-700 mb-1">Image de fond</label>
          <div className="flex items-center gap-3 flex-wrap mb-2">
            <input type="file" accept="image/*" onChange={uploadHero} />
            <Button type="button" className="bg-gray-100 text-gray-800" onClick={pickFromGallery}>Sélectionner depuis la galerie</Button>
          </div>
          {draft.hero_image_url && <img src={draft.hero_image_url} alt="hero" className="h-32 object-contain border rounded" />}
        </div>

        <div>
          <label className="block text-sm text-gray-700 mb-1">Format de police</label>
          <select className="w-full border rounded h-10 px-3" value={draft.font_family} onChange={(e)=>setDraft(d=>({...d, font_family: e.target.value}))}>
            <option value="system">System</option>
            <option value="serif">Serif</option>
            <option value="mono">Monospace</option>
          </select>
        </div>
        <div>
          <label className="block text-sm text-gray-700 mb-1">Couleur Titre 1</label>
          <Input type="color" value={draft.title1_color} onChange={(e)=>setDraft(d=>({...d, title1_color: e.target.value}))} />
        </div>
        <div>
          <label className="block text-sm text-gray-700 mb-1">Couleur Titre 2</label>
          <Input type="color" value={draft.title2_color} onChange={(e)=>setDraft(d=>({...d, title2_color: e.target.value}))} />
        </div>
        <div>
          <label className="block text-sm text-gray-700 mb-1">Couleur Sous-titre</label>
          <Input type="color" value={draft.subtitle_color} onChange={(e)=>setDraft(d=>({...d, subtitle_color: e.target.value}))} />
        </div>

        <div>
          <label className="block text-sm text-gray-700 mb-1">Format</label>
          <select className="w-full border rounded h-10 px-3" value={draft.hero_image_format || ''} onChange={(e)=>setDraft(d=>({...d, hero_image_format: e.target.value}))}>
            <option value="">(laisser inchangé)</option>
            <option value="webp">WebP</option>
            <option value="jpeg">JPEG</option>
          </select>
        </div>
        <div>
          <label className="block text-sm text-gray-700 mb-1">Qualité (50-95)</label>
          <Input type="number" min="50" max="95" value={draft.hero_image_quality} onChange={(e)=>setDraft(d=>({...d, hero_image_quality: Number(e.target.value)}))} />
        </div>
        <div>
          <label className="block text-sm text-gray-700 mb-1">Largeur cible (px)</label>
          <Input type="number" min="600" max="3840" value={draft.hero_image_width} onChange={(e)=>setDraft(d=>({...d, hero_image_width: Number(e.target.value)}))} />
        </div>
        <div className="flex items-end gap-2">
          <Button type="button" className="bg-gray-200 text-gray-800" onClick={convertHero}>Convertir</Button>
          <Button type="button" className="bg-gray-100" onClick={()=>setPreviewOpen(true)}>Aperçu plein écran</Button>
        </div>
      </div>

      <div className="mt-6 flex gap-3">
        <Button className="bg-yellow-600 text-black" onClick={save}>Enregistrer (Brouillon)</Button>
        <Button className="bg-green-600 text-white" onClick={publish}>Publier</Button>
        <Button className="bg-gray-200 text-gray-800" onClick={load}>Réinitialiser</Button>
      </div>

      <div className="mt-8">
        <h3 className="font-semibold mb-2">Aperçu (brouillon, non publié)</h3>
        {heroPreview}
      </div>

      {previewOpen && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={()=>setPreviewOpen(false)}>
          <div className="bg-white rounded-xl w-full h-[90vh] max-w-7xl p-2" onClick={(e)=>e.stopPropagation()}>
            {heroPreview}
            <div className="mt-2 text-right">
              <Button className="bg-gray-200 text-gray-800" onClick={()=>setPreviewOpen(false)}>Fermer</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettingsPage;